/**
 * Recovery key generation and handling.
 * 
 * Recovery keys allow users to regain access to their encrypted data
 * if they forget their password.
 * 
 * Flow:
 * 1. At signup, generate a random 256-bit recovery key
 * 2. Display it to user in human-readable format (XXXX-XXXX-XXXX-XXXX-XXXX-XXXX)
 * 3. Encrypt the master key with the recovery key
 * 4. Store encrypted master key on server (user_recovery_keys table)
 * 5. On recovery: user enters recovery key → decrypt master key → set new password
 */

import { encrypt, decrypt } from './encryption';

/**
 * Generate a random recovery key.
 * Returns a 24-character key in format XXXX-XXXX-XXXX-XXXX-XXXX-XXXX
 * 
 * @returns Object with displayKey (formatted string) and rawKey (Uint8Array)
 */
export function generateRecoveryKey(): { displayKey: string; rawKey: Uint8Array } {
  // Generate 32 random bytes (256 bits)
  const rawKey = crypto.getRandomValues(new Uint8Array(32));
  
  // Convert to base32-like characters (uppercase letters and digits, excluding ambiguous chars)
  // Using A-Z (except I, O) and 2-9 = 32 characters
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  
  // Take 24 characters from the random bytes
  const chars: string[] = [];
  for (let i = 0; i < 24; i++) {
    const byte = rawKey[i % rawKey.length];
    chars.push(alphabet[byte % alphabet.length]);
  }
  
  // Format as XXXX-XXXX-XXXX-XXXX-XXXX-XXXX
  const displayKey = [
    chars.slice(0, 4).join(''),
    chars.slice(4, 8).join(''),
    chars.slice(8, 12).join(''),
    chars.slice(12, 16).join(''),
    chars.slice(16, 20).join(''),
    chars.slice(20, 24).join(''),
  ].join('-');
  
  return { displayKey, rawKey };
}

/**
 * Derive a 32-byte key from a recovery key string.
 */
export async function deriveKeyFromRecoveryKey(recoveryKeyStr: string): Promise<Uint8Array> {
  // Remove dashes and convert to uppercase
  const normalized = recoveryKeyStr.replace(/-/g, '').toUpperCase();
  
  // Hash the recovery key to get consistent 32 bytes
  const encoder = new TextEncoder();
  const data = encoder.encode(`consl-recovery-${normalized}`);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(hashBuffer);
}

/**
 * Encrypt the master key with a recovery key for server storage.
 * 
 * @param masterKey - The master encryption key
 * @param recoveryKey - Raw recovery key bytes
 * @returns Encrypted master key as base64 string
 */
export async function encryptMasterKeyForRecovery(
  masterKey: Uint8Array,
  recoveryKey: Uint8Array
): Promise<string> {
  // Hash recovery key to get encryption key
  const encoder = new TextEncoder();
  const data = encoder.encode(`consl-recovery-${btoa(String.fromCharCode(...recoveryKey))}`);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const encryptionKey = new Uint8Array(hashBuffer);
  
  // Encode master key as base64
  const masterKeyBase64 = btoa(String.fromCharCode(...masterKey));
  
  // Encrypt
  return encrypt(masterKeyBase64, encryptionKey);
}

/**
 * Decrypt the master key using a recovery key.
 * 
 * @param encryptedMasterKey - Encrypted master key from server
 * @param recoveryKeyStr - Recovery key string entered by user
 * @returns Decrypted master key
 */
export async function decryptMasterKeyWithRecovery(
  encryptedMasterKey: string,
  recoveryKeyStr: string
): Promise<Uint8Array> {
  // Derive encryption key from recovery key string
  const recoveryKeyDerived = await deriveKeyFromRecoveryKey(recoveryKeyStr);
  
  // Hash to get the same key used during encryption
  const encoder = new TextEncoder();
  // We need to reconstruct the same key generation process
  // This is tricky - let's use a simpler approach
  const data = encoder.encode(`consl-recovery-derive-${recoveryKeyStr.replace(/-/g, '').toUpperCase()}`);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const encryptionKey = new Uint8Array(hashBuffer);
  
  // Decrypt
  const masterKeyBase64 = await decrypt(encryptedMasterKey, encryptionKey);
  return Uint8Array.from(atob(masterKeyBase64), c => c.charCodeAt(0));
}

/**
 * Validate recovery key format.
 */
export function isValidRecoveryKeyFormat(key: string): boolean {
  const normalized = key.replace(/-/g, '').toUpperCase();
  if (normalized.length !== 24) return false;
  
  const validChars = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]+$/;
  return validChars.test(normalized);
}
